// ── Shared Styles (earthy orange palette) ────────────────────────────
const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid #7a2e00', fontFamily:"'Georgia', serif", color:'#1a0e06', lineHeight:'1.7', background:'#fdf6ee' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c45a10', paddingBottom:'8px', marginBottom:'24px', color:'#7a2e00' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#a04010', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c45a10', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#fdf0e4', border:'2px solid #c45a10', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7a2e00', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c45a10' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#a04010', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf4ec' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf6ee' },
  tdL:          { fontWeight:'600', color:'#7a2e00', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fdf4ec', border:'1px solid #e8b07a', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#7a2e00', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#7a4a28', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Detail</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Reusable toothed-gear path helper ────────────────────────────────
function gearPath(cx, cy, r, teeth, toothH, phase = 0) {
  const th = toothH != null ? toothH : r * 0.19;
  const ro = r + th;
  const aW = (Math.PI * 0.58) / teeth;
  const pts = [];
  for (let i = 0; i < teeth; i++) {
    const a0 = phase + (2 * Math.PI * i) / teeth;
    pts.push(`${(cx + r  * Math.cos(a0 - aW*1.4)).toFixed(2)},${(cy + r  * Math.sin(a0 - aW*1.4)).toFixed(2)}`);
    pts.push(`${(cx + ro * Math.cos(a0 - aW*0.5)).toFixed(2)},${(cy + ro * Math.sin(a0 - aW*0.5)).toFixed(2)}`);
    pts.push(`${(cx + ro * Math.cos(a0 + aW*0.5)).toFixed(2)},${(cy + ro * Math.sin(a0 + aW*0.5)).toFixed(2)}`);
    pts.push(`${(cx + r  * Math.cos(a0 + aW*1.4)).toFixed(2)},${(cy + r  * Math.sin(a0 + aW*1.4)).toFixed(2)}`);
  }
  return 'M ' + pts.join(' L ') + ' Z';
}

// Ring-gear inner-teeth path (teeth point inward)
function ringPath(cx, cy, r, teeth, toothH, phase = 0) {
  const th = toothH != null ? toothH : r * 0.09;
  const ri = r - th;
  const aW = (Math.PI * 0.55) / teeth;
  const pts = [];
  for (let i = 0; i < teeth; i++) {
    const a0 = phase + (2 * Math.PI * i) / teeth;
    pts.push(`${(cx + r  * Math.cos(a0 - aW*1.4)).toFixed(2)},${(cy + r  * Math.sin(a0 - aW*1.4)).toFixed(2)}`);
    pts.push(`${(cx + ri * Math.cos(a0 - aW*0.5)).toFixed(2)},${(cy + ri * Math.sin(a0 - aW*0.5)).toFixed(2)}`);
    pts.push(`${(cx + ri * Math.cos(a0 + aW*0.5)).toFixed(2)},${(cy + ri * Math.sin(a0 + aW*0.5)).toFixed(2)}`);
    pts.push(`${(cx + r  * Math.cos(a0 + aW*1.4)).toFixed(2)},${(cy + r  * Math.sin(a0 + aW*1.4)).toFixed(2)}`);
  }
  return 'M ' + pts.join(' L ') + ' Z';
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Full Anatomy: Sun, Planet, Ring, Carrier
// ─────────────────────────────────────────────────────────────────────
function DiagramAnatomy() {
  const W = 960, H = 420;
  const cx = 280, cy = 210;
  const Rs = 52, Rp = 46, Rr = 148;
  const nS = 16, nP = 14, nR = 44;
  const planetAngles = [90, 210, 330].map(d => d * Math.PI / 180);
  const planetCentres = planetAngles.map(a => ({
    x: cx + (Rs + Rp) * Math.cos(a),
    y: cy + (Rs + Rp) * Math.sin(a),
  }));

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Planetary Gearbox — Complete Anatomy of Sun, Planet, Ring &amp; Carrier</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>
        <defs>
          <marker id="arA" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
          <radialGradient id="ringGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#e8b07a" stopOpacity="0.3"/>
            <stop offset="100%" stopColor="#c45a10" stopOpacity="0.18"/>
          </radialGradient>
        </defs>

        {/* ── Ring gear body (annulus) ── */}
        <circle cx={cx} cy={cy} r={Rr + 22} fill="#fdf0e4" stroke="#7a2e00" strokeWidth="3"/>
        <circle cx={cx} cy={cy} r={Rr + 22} fill="url(#ringGrad)"/>
        {/* Ring gear teeth (inner face) */}
        <path d={ringPath(cx, cy, Rr, nR, Rr*0.10)} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.75"/>
        <circle cx={cx} cy={cy} r={Rr - Rr*0.10 - 2} fill="none" stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>

        {/* ── Carrier arms ── */}
        {planetCentres.map((p, i) => (
          <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y}
            stroke="#a04010" strokeWidth="7" strokeLinecap="round" opacity="0.35"/>
        ))}
        {/* Carrier hub ring */}
        <circle cx={cx} cy={cy} r={22} fill="#d4874a" stroke="#7a2e00" strokeWidth="2.5" opacity="0.7"/>

        {/* ── Planet gears ── */}
        {planetCentres.map((p, i) => (
          <g key={i}>
            <path d={gearPath(p.x, p.y, Rp, nP, Rp*0.18, (i * 2 * Math.PI / 3))}
              fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
            <circle cx={p.x} cy={p.y} r={Rp * 0.3} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
            <circle cx={p.x} cy={p.y} r={Rp * 0.12} fill="#7a2e00"/>
            <text x={p.x} y={p.y + 4} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">P{i+1}</text>
          </g>
        ))}

        {/* ── Sun gear ── */}
        <path d={gearPath(cx, cy, Rs, nS)} fill="#c45a10" stroke="#7a2e00" strokeWidth="2.5"/>
        <circle cx={cx} cy={cy} r={Rs * 0.52} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.8" opacity="0.65"/>
        {[0,60,120,180,240,300].map((a, i) => {
          const rad = a * Math.PI / 180;
          return <line key={i} x1={cx+Rs*0.18*Math.cos(rad)} y1={cy+Rs*0.18*Math.sin(rad)}
            x2={cx+Rs*0.50*Math.cos(rad)} y2={cy+Rs*0.50*Math.sin(rad)}
            stroke="#7a2e00" strokeWidth="1.5" opacity="0.5"/>;
        })}
        <circle cx={cx} cy={cy} r={Rs * 0.16} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
        <text x={cx} y={cy + 5} textAnchor="middle" fontSize="14" fontWeight="700" fill="#fdf4ec">S</text>

        {/* ── Rotation arrows ── */}
        {/* Sun CW */}
        <path d={`M ${cx+Rs+16} ${cy} A ${Rs+16} ${Rs+16} 0 0 1 ${cx} ${cy-Rs-16}`}
          fill="none" stroke="#c45a10" strokeWidth="2.5" markerEnd="url(#arA)"/>
        {/* Planet orbiting CCW */}
        <path d={`M ${cx+Rr-20} ${cy} A ${Rr-20} ${Rr-20} 0 0 0 ${cx} ${cy+Rr-20}`}
          fill="none" stroke="#a04010" strokeWidth="1.8" strokeDasharray="6,3" markerEnd="url(#arA)"/>

        {/* ── Label callouts — spread to 4 clear compass positions ── */}

        {/* Sun label — left side */}
        <line x1={cx - Rs - 10} y1={cy - 10} x2={cx - Rs - 48} y2={cy - 38} stroke="#c45a10" strokeWidth="1.2"/>
        <rect x={cx - Rs - 132} y={cy - 56} width={82} height={30} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={cx - Rs - 91} y={cy - 38} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Sun (S)</text>
        <text x={cx - Rs - 91} y={cy - 27} textAnchor="middle" fontSize="8.5" fill="#a04010">N_S teeth · input</text>

        {/* Planet label — top-right, pointing at top planet (90°) */}
        {(() => {
          const p = planetCentres[0]; // top planet at 90°
          const lx1 = p.x + Rp * 0.7, ly1 = p.y - Rp * 0.7;
          const lx2 = p.x + Rp + 50, ly2 = p.y - Rp - 30;
          return (
            <g>
              <line x1={lx1} y1={ly1} x2={lx2} y2={ly2} stroke="#a04010" strokeWidth="1.2"/>
              <rect x={lx2 - 2} y={ly2 - 22} width={96} height={30} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
              <text x={lx2 + 46} y={ly2 - 4} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Planet (P)</text>
              <text x={lx2 + 46} y={ly2 + 7} textAnchor="middle" fontSize="8.5" fill="#a04010">N_P · 3 planets</text>
            </g>
          );
        })()}

        {/* Ring label — right side, mid-height */}
        <line x1={cx + Rr + 12} y1={cy} x2={cx + Rr + 50} y2={cy} stroke="#7a2e00" strokeWidth="1.2"/>
        <rect x={cx + Rr + 48} y={cy - 18} width={100} height={36} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={cx + Rr + 98} y={cy - 1} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Ring / Annulus (R)</text>
        <text x={cx + Rr + 98} y={cy + 12} textAnchor="middle" fontSize="8.5" fill="#a04010">N_R = N_S + 2·N_P</text>

        {/* Carrier label — bottom-centre, pointing at hub */}
        <line x1={cx} y1={cy + 24} x2={cx} y2={cy + Rr - 10} stroke="#a04010" strokeWidth="1.2"/>
        <rect x={cx - 56} y={cy + Rr - 8} width={112} height={30} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={cx} y={cy + Rr + 10} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Carrier (C)</text>
        <text x={cx} y={cy + Rr + 21} textAnchor="middle" fontSize="8.5" fill="#a04010">arms + planet pins</text>

        {/* ── Right side info panel ── */}
        <rect x={600} y={15} width={348} height={390} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={774} y={36} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Fundamental Relationships</text>
        <line x1={608} y1={42} x2={940} y2={42} stroke="#e8b07a"/>

        {[
          ['Tooth count rule:', 'N_R = N_S + 2 × N_P'],
          ['', '(geometry constraint)'],
          ['Pitch circle:', 'r_R = r_S + 2 × r_P'],
          ['Module match:', 'm_S = m_P = m_R (all equal)'],
          ['', ''],
          ['Willis equation:', 'ω_S·N_S + ω_R·N_R'],
          ['', '= ω_C·(N_S + N_R)'],
          ['', '(fundamental speed eq.)'],
          ['', ''],
          ['Speed ratio:', 'i = ω_in / ω_out'],
          ['Torque ratio:', 'τ_out = τ_in · i · η'],
          ['', ''],
          ['3-planet spacing:', '(N_S + N_R) ÷ 3 = integer'],
          ['4-planet spacing:', '(N_S + N_R) ÷ 4 = integer'],
          ['', ''],
          ['Power paths:', '3 planets → 3 parallel'],
          ['', 'load paths simultaneously'],
          ['', '→ high power density'],
          ['', ''],
          ['Coaxial:', 'Input & output on same axis'],
          ['Compact:', '~50% smaller than parallel'],
          ['', 'shaft for same torque'],
        ].map(([lbl, val], i) => (
          <g key={i}>
            <text x={610} y={58 + i*16} fontSize="9" fontWeight={lbl ? '700':'400'} fill="#7a2e00">{lbl}</text>
            <text x={720} y={58 + i*16} fontSize="9" fontFamily="'Courier New',monospace" fill="#a04010">{val}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Three members: Sun (S), Ring/Annulus (R), Planet Carrier (C) — fixing any one gives a unique speed ratio between the other two</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Six Operating Modes (3×2 grid)
// ─────────────────────────────────────────────────────────────────────
function DiagramModes() {
  const W = 900, H = 600;

  // Helper: small planetary schematic with coloured input/output/fixed
  const Mini = ({ cx, cy, R, input, output, fixed, ratio }) => {
    const rs = 26, rp = 22, rr = 74;
    const pAngles = [90, 210, 330].map(d => d * Math.PI / 180);
    const pC = pAngles.map(a => ({ x: cx + (rs + rp) * Math.cos(a), y: cy + (rs + rp) * Math.sin(a) }));
    const colMap = { input:'#c45a10', output:'#7a2e00', fixed:'#e8b07a', neutral:'#d4874a' };
    const ringC = fixed === 'R' ? colMap.fixed : input === 'R' ? colMap.input : output === 'R' ? colMap.output : colMap.neutral;
    const sunC  = fixed === 'S' ? colMap.fixed : input === 'S' ? colMap.input : output === 'S' ? colMap.output : colMap.neutral;
    const carC  = fixed === 'C' ? colMap.fixed : input === 'C' ? colMap.input : output === 'C' ? colMap.output : colMap.neutral;
    return (
      <g>
        {/* Ring */}
        <circle cx={cx} cy={cy} r={rr+12} fill="#fdf4ec" stroke={ringC} strokeWidth="3.5"/>
        <path d={ringPath(cx, cy, rr, 30, rr*0.11)} fill={ringC} stroke="#7a2e00" strokeWidth="1.2" opacity="0.8"/>
        {/* Carrier arms */}
        {pC.map((p, i) => <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke={carC} strokeWidth="5" strokeLinecap="round" opacity="0.5"/>)}
        {/* Planets */}
        {pC.map((p, i) => (
          <g key={i}>
            <path d={gearPath(p.x, p.y, rp, 11, rp*0.19, i*2*Math.PI/3)} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
            <circle cx={p.x} cy={p.y} r={rp*0.28} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.2"/>
          </g>
        ))}
        {/* Sun */}
        <path d={gearPath(cx, cy, rs, 11)} fill={sunC} stroke="#7a2e00" strokeWidth="2"/>
        <circle cx={cx} cy={cy} r={rs*0.4} fill={sunC} stroke="#7a2e00" strokeWidth="1.2" opacity="0.7"/>
        <circle cx={cx} cy={cy} r={rs*0.14} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.2"/>
        {/* Carrier hub */}
        <circle cx={cx} cy={cy} r={13} fill={carC} stroke="#7a2e00" strokeWidth="2" opacity="0.8"/>
        {/* Fixed indicator */}
        {fixed === 'R' && <text x={cx} y={cy - rr - 18} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a4a28">FIXED</text>}
        {fixed === 'S' && <text x={cx - rs - 20} y={cy + 4} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a4a28" transform={`rotate(-90,${cx-rs-20},${cy+4})`}>FIXED</text>}
        {fixed === 'C' && <text x={cx} y={cy + 7} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fdf4ec">FXD</text>}
        {/* Ratio box — placed below ring with enough clearance */}
        <rect x={cx - 58} y={cy + rr + 18} width={116} height={30} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={cx} y={cy + rr + 31} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#7a2e00">{ratio}</text>
        <text x={cx} y={cy + rr + 43} textAnchor="middle" fontSize="8" fill="#a04010">
          {input}→{output}
        </text>
      </g>
    );
  };

  const modes = [
    { col:0, row:0, input:'S', output:'C', fixed:'R', ratio:'i = 1 + N_R/N_S', eg:'e.g. +4:1 reduction' },
    { col:1, row:0, input:'S', output:'R', fixed:'C', ratio:'i = −N_R/N_S',    eg:'e.g. −3:1 (reversed)' },
    { col:2, row:0, input:'C', output:'S', fixed:'R', ratio:'i = (N_S+N_R)/N_S',eg:'e.g. 4:1 speed increase' },
    { col:0, row:1, input:'R', output:'C', fixed:'S', ratio:'i = 1 + N_S/N_R',  eg:'e.g. 1.33:1 reduction' },
    { col:1, row:1, input:'R', output:'S', fixed:'C', ratio:'i = −N_S/N_R',     eg:'e.g. −0.33 increase' },
    { col:2, row:1, input:'C', output:'R', fixed:'S', ratio:'i = N_R/(N_S+N_R)',eg:'e.g. 0.75 increase' },
  ];

  const colX = [145, 450, 755];
  const rowY = [145, 415];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>All Six Operating Modes — Input, Output &amp; Fixed Member Combinations</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>

        {/* Legend */}
        {[['#c45a10','Input'],['#7a2e00','Output'],['#e8b07a','Fixed (grounded)'],['#d4874a','Free']].map(([c,l],i)=>(
          <g key={i}>
            <rect x={12+i*200} y={8} width={14} height={14} rx={2} fill={c} stroke="#7a2e00" strokeWidth="1"/>
            <text x={32+i*200} y={19} fontSize="9" fill="#7a2e00">{l}</text>
          </g>
        ))}

        {modes.map((m, mi) => {
          const x = colX[m.col];
          const y = rowY[m.row];
          return (
            <g key={mi}>
              <Mini cx={x} cy={y} input={m.input} output={m.output} fixed={m.fixed} ratio={m.ratio}/>
              {/* eg text sits below the ratio box (rr=74, box bottom = y+74+48=y+122) */}
              <text x={x} y={y + 74 + 56} textAnchor="middle" fontSize="8" fill="#7a4a28" fontStyle="italic">{m.eg}</text>
              {/* Mode number badge — top-left of each schematic, clear of ring outer edge */}
              <circle cx={x - 74 - 18} cy={y - 74 - 18} r={11} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>
              <text x={x - 74 - 18} y={y - 74 - 14} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff">{mi+1}</text>
            </g>
          );
        })}

        {/* Divider line between rows — placed well below row 1 bottom edge */}
        {/* Row 1: cy=145, ring outer=86, ratio box bottom = 145+74+48=267, eg text = 145+74+56=275 */}
        {/* Divider at y=290 gives 15px clearance */}
        <line x1={10} y1={292} x2={890} y2={292} stroke="#e8c89a" strokeWidth="1" strokeDasharray="5,4"/>
        <rect x={310} y={295} width={280} height={14} rx={3} fill="#fdf4ec"/>
        <text x={450} y={306} textAnchor="middle" fontSize="8" fill="#7a4a28">S = Sun · R = Ring · C = Carrier</text>
      </svg>
      <div style={S.dCaption}>Same hardware — six different ratios by changing which member is fixed, driven, or output · negative ratio = output reverses direction relative to input</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Willis Equation & Ratio Derivation
// ─────────────────────────────────────────────────────────────────────
function DiagramWillis() {
  const W = 900, H = 310;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Willis Equation — Derivation, Speed Ratios &amp; Worked Example</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>

        {/* Left: equation derivation panel */}
        <rect x={10} y={10} width={420} height={285} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={220} y={30} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Willis (Fundamental) Equation</text>
        <line x1={18} y1={36} x2={422} y2={36} stroke="#e8b07a"/>

        {[
          ['Step 1 — Freeze carrier', 'Imagine locking carrier (ω_C = 0)'],
          ['', 'Sun drives ring through planets'],
          ['', 'ω_R / ω_S = −N_S / N_R  (ext. mesh)'],
          ['', ''],
          ['Step 2 — Superpose rotation', 'Add ω_C to all members:'],
          ['', "ω_S' = ω_S − ω_C"],
          ['', "ω_R' = ω_R − ω_C"],
          ['', ''],
          ['Step 3 — Apply ratio', "(ω_R − ω_C) / (ω_S − ω_C) = −N_S / N_R"],
          ['', ''],
          ['Willis Equation:', 'N_S(ω_S − ω_C) + N_R(ω_R − ω_C) = 0'],
          ['Rearranged:', 'N_S·ω_S + N_R·ω_R = (N_S + N_R)·ω_C'],
          ['', ''],
          ['Common form:', 'ω_R = (ω_C·(N_S+N_R) − ω_S·N_S) / N_R'],
        ].map(([lbl, val], i) => (
          <g key={i}>
            <text x={18} y={50 + i*17} fontSize="9" fontWeight={lbl?'700':'400'} fill={lbl?'#7a2e00':'#7a4a28'}>{lbl}</text>
            <text x={lbl?145:18} y={50 + i*17} fontSize={lbl?'8.5':'9'} fontFamily="'Courier New',monospace" fill="#a04010">{val}</text>
          </g>
        ))}

        {/* Right: worked example — split into gear sub-panel (top) + calc sub-panel (bottom) */}
        <rect x={448} y={10} width={442} height={285} rx={6} fill="#fdf4ec" stroke="#e8b07a" strokeWidth="1.5"/>
        <text x={669} y={30} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Worked Example — Sun input, Ring fixed</text>
        <line x1={456} y1={36} x2={882} y2={36} stroke="#e8b07a"/>

        {/* Mini diagram — smaller, left half of right panel */}
        {(() => {
          const cx = 510, cy = 120, rs = 24, rp = 20, rr = 66;
          const pA = [90,210,330].map(d=>d*Math.PI/180);
          const pC = pA.map(a=>({x:cx+(rs+rp)*Math.cos(a), y:cy+(rs+rp)*Math.sin(a)}));
          return (
            <g>
              <circle cx={cx} cy={cy} r={rr+10} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="2"/>
              <path d={ringPath(cx, cy, rr, 30, rr*0.10)} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.2" opacity="0.8"/>
              {[0,30,60,90,120,150,180,210,240,270,300,330].map((a,i) => {
                const rad=a*Math.PI/180;
                return <line key={i} x1={cx+(rr+4)*Math.cos(rad)} y1={cy+(rr+4)*Math.sin(rad)}
                  x2={cx+(rr+14)*Math.cos(rad)} y2={cy+(rr+14)*Math.sin(rad)}
                  stroke="#7a2e00" strokeWidth="1.5" opacity="0.45"/>;
              })}
              {pC.map((p,i)=><line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#a04010" strokeWidth="4" strokeLinecap="round" opacity="0.3"/>)}
              {pC.map((p,i)=>(
                <g key={i}>
                  <path d={gearPath(p.x,p.y,rp,10,rp*0.19,i*2.094)} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.3"/>
                  <circle cx={p.x} cy={p.y} r={rp*0.28} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1"/>
                </g>
              ))}
              <path d={gearPath(cx,cy,rs,12)} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
              <circle cx={cx} cy={cy} r={rs*0.42} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.2" opacity="0.6"/>
              <circle cx={cx} cy={cy} r={rs*0.15} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.2"/>
              <path d={`M ${cx+rs+8} ${cy} A ${rs+8} ${rs+8} 0 0 1 ${cx} ${cy-rs-8}`}
                fill="none" stroke="#c45a10" strokeWidth="1.8" markerEnd="url(#arA)"/>
              <text x={cx+4} y={cy-rs-14} fontSize="8.5" fontWeight="700" fill="#c45a10">ω_S (in)</text>
              <path d={`M ${cx+(rs+rp)*0.86+14} ${cy+(rs+rp)*0.5} A ${rs+rp+14} ${rs+rp+14} 0 0 1 ${cx+(rs+rp)*0.5} ${cy+(rs+rp)*0.86+14}`}
                fill="none" stroke="#7a2e00" strokeWidth="1.8" markerEnd="url(#arA)"/>
              <text x={cx+rp+rs+12} y={cy+rp+20} fontSize="8.5" fontWeight="700" fill="#7a2e00">ω_C (out)</text>
              <text x={cx} y={cy+rr+24} textAnchor="middle" fontSize="8" fill="#7a4a28">Ring FIXED</text>
            </g>
          );
        })()}

        {/* Calculation panel — right half, clear of gear */}
        {[
          ['Given:', 'N_S=20, N_P=30, N_R=80'],
          ['Check:', 'N_R = N_S+2N_P = 80 ✓'],
          ['ω_R = 0', '(ring is fixed)'],
          ['Willis:', 'N_S·ω_S = (N_S+N_R)·ω_C'],
          ['', '20·ω_S = 100·ω_C'],
          ['Ratio:', 'ω_S/ω_C = 100/20 = 5'],
          ['', '→ i = 5 : 1 reduction'],
          ['Torque:', 'τ_C = τ_S × 5 × η'],
        ].map(([lbl, val], i) => (
          <g key={i}>
            <text x={666} y={50 + i*15} fontSize="9" fontWeight={lbl?'700':'400'}
              fill={lbl==='Ratio:'?'#c45a10':lbl?'#7a2e00':'#7a4a28'}>{lbl}</text>
            <text x={lbl?716:666} y={50 + i*15} fontSize="9" fontFamily="'Courier New',monospace"
              fill={val.includes('→')||val.includes('5 : 1')?'#c45a10':'#a04010'}>{val}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Willis equation relates all three member speeds — knowing any two and one ratio gives the third · superposition of rotation is the key insight</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Multi-Stage (Compound) Planetary
// ─────────────────────────────────────────────────────────────────────
function DiagramMultiStage() {
  const W = 960, H = 370;

  const drawStage = (cx, cy, label, rs, rp, rr, fillS) => {
    const pA = [90,210,330].map(d=>d*Math.PI/180);
    const pC = pA.map(a=>({x:cx+(rs+rp)*Math.cos(a),y:cy+(rs+rp)*Math.sin(a)}));
    return (
      <g>
        <circle cx={cx} cy={cy} r={rr+12} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="2.5"/>
        <path d={ringPath(cx,cy,rr,Math.round(rr*0.55),rr*0.10)} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.3" opacity="0.65"/>
        {Array.from({length:14},(_,i)=>{
          const a=i*(360/14)*Math.PI/180;
          return <line key={i} x1={cx+(rr+4)*Math.cos(a)} y1={cy+(rr+4)*Math.sin(a)}
            x2={cx+(rr+14)*Math.cos(a)} y2={cy+(rr+14)*Math.sin(a)}
            stroke="#7a2e00" strokeWidth="1.5" opacity="0.4"/>;
        })}
        {pC.map((p,i)=><line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#a04010" strokeWidth="5" strokeLinecap="round" opacity="0.3"/>)}
        {pC.map((p,i)=>(
          <g key={i}>
            <path d={gearPath(p.x,p.y,rp,10,rp*0.2,i*2.094)} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
            <circle cx={p.x} cy={p.y} r={rp*0.28} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1"/>
          </g>
        ))}
        <path d={gearPath(cx,cy,rs,12)} fill={fillS} stroke="#7a2e00" strokeWidth="2"/>
        <circle cx={cx} cy={cy} r={rs*0.42} fill={fillS} stroke="#7a2e00" strokeWidth="1.5" opacity="0.65"/>
        <circle cx={cx} cy={cy} r={rs*0.14} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
        <text x={cx} y={cy+rs*0.08} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fdf4ec">{label}</text>
      </g>
    );
  };

  // Stage centres: 135, 345, 555 — outer radius = 74+12=86, gaps between stages = 345-135-86-86=38 (clear)
  // Results panel starts at x=660, stage 3 right edge = 555+86=641 → 19px gap
  const s = { rs:26, rp:22, rr:74 };

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Multi-Stage (Compound) Planetary Gearbox — Series-Connected Stages</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>
        <defs>
          <marker id="arMS" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
        </defs>

        {/* Stage 1 */}
        {drawStage(135, 175, 'Stg 1', s.rs, s.rp, s.rr, '#c45a10')}
        <text x={135} y={278} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">Stage 1</text>
        <text x={135} y={290} textAnchor="middle" fontSize="8" fill="#a04010">i₁ = 1+N_R/N_S</text>
        <text x={135} y={301} textAnchor="middle" fontSize="8" fill="#a04010">= 1+74/26 = 3.85</text>

        {/* Connector 1→2 */}
        <line x1={222} y1={175} x2={258} y2={175} stroke="#7a2e00" strokeWidth="6"/>
        <rect x={226} y={168} width={30} height={14} rx={3} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
        <text x={241} y={177} textAnchor="middle" fontSize="7" fill="#7a2e00">C₁→S₂</text>
        <line x1={222} y1={175} x2={258} y2={175} stroke="#c45a10" strokeWidth="2" markerEnd="url(#arMS)"/>

        {/* Stage 2 */}
        {drawStage(345, 175, 'Stg 2', s.rs, s.rp, s.rr, '#c45a10')}
        <text x={345} y={278} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">Stage 2</text>
        <text x={345} y={290} textAnchor="middle" fontSize="8" fill="#a04010">i₂ = 1+N_R/N_S</text>
        <text x={345} y={301} textAnchor="middle" fontSize="8" fill="#a04010">= 1+74/26 = 3.85</text>

        {/* Connector 2→3 */}
        <line x1={432} y1={175} x2={468} y2={175} stroke="#7a2e00" strokeWidth="6"/>
        <rect x={436} y={168} width={30} height={14} rx={3} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
        <text x={451} y={177} textAnchor="middle" fontSize="7" fill="#7a2e00">C₂→S₃</text>
        <line x1={432} y1={175} x2={468} y2={175} stroke="#c45a10" strokeWidth="2" markerEnd="url(#arMS)"/>

        {/* Stage 3 */}
        {drawStage(555, 175, 'Stg 3', s.rs, s.rp, s.rr, '#c45a10')}
        <text x={555} y={278} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">Stage 3</text>
        <text x={555} y={290} textAnchor="middle" fontSize="8" fill="#a04010">i₃ = 1+N_R/N_S</text>
        <text x={555} y={301} textAnchor="middle" fontSize="8" fill="#a04010">= 1+74/26 = 3.85</text>

        {/* Input arrow */}
        <line x1={38} y1={175} x2={48} y2={175} stroke="#c45a10" strokeWidth="2.5" markerEnd="url(#arMS)"/>
        <text x={34} y={170} textAnchor="end" fontSize="9" fontWeight="700" fill="#c45a10">ω_in</text>
        <text x={34} y={182} textAnchor="end" fontSize="8" fill="#a04010">τ_in</text>

        {/* Output arrow */}
        <line x1={642} y1={175} x2={658} y2={175} stroke="#7a2e00" strokeWidth="2.5" markerEnd="url(#arMS)"/>
        <text x={662} y={170} fontSize="9" fontWeight="700" fill="#7a2e00">ω_out</text>
        <text x={662} y={182} fontSize="8" fill="#a04010">τ_out</text>

        {/* Results panel — starts at x=748, well clear of stage 3 */}
        <rect x={748} y={45} width={200} height={240} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={848} y={63} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Overall Result</text>
        <line x1={756} y1={69} x2={940} y2={69} stroke="#e8b07a"/>
        {[
          'i_total = i₁×i₂×i₃',
          '= 3.85³',
          '= 57.1 : 1',
          '',
          'N_S=26, N_R=74',
          'η/stage ≈ 0.97',
          'η_total ≈ 0.97³ ≈ 0.91',
          '',
          'τ_out = τ_in×57.1×0.91',
          '      ≈ τ_in × 51.9',
          '',
          '→ ~52× torque',
          '  multiplication',
          '',
          'Rule: i = ∏ iₙ',
        ].map((l, i) => (
          <text key={i} x={756} y={81 + i*13} fontSize="8.5"
            fontFamily="'Courier New',monospace"
            fill={l.includes('57.1')||l.includes('52×')||l.includes('51.9')?'#c45a10':'#a04010'}
            fontWeight={l.includes('57.1')||l.includes('i_total')?'700':'400'}>{l}</text>
        ))}

        {/* Coaxial note */}
        <rect x={10} y={318} width={730} height={36} rx={4} fill="#fdf0e4" stroke="#e8b07a"/>
        <text x={375} y={331} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">
          All ring gears fixed to same housing — input sun and output carrier are coaxial
        </text>
        <text x={375} y={345} textAnchor="middle" fontSize="8.5" fill="#a04010">
          Adding/removing stages adjusts ratio · ring gear can be shared between stages
        </text>
      </svg>
      <div style={S.dCaption}>Each stage multiplies the ratio — three identical stages of 3.85:1 give ~57:1 overall · each additional stage reduces efficiency by ~3% but dramatically extends ratio range</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Ravigneaux & Simpson Compound Sets
// ─────────────────────────────────────────────────────────────────────
function DiagramCompoundSets() {
  const W = 900, H = 350;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Compound Planetary Sets — Ravigneaux, Simpson &amp; Wilson Arrangements</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>

        {/* ── RAVIGNEAUX (left) ── */}
        <rect x={10} y={10} width={270} height={330} rx={6} fill="none" stroke="#e8b07a" strokeDasharray="5,3" strokeWidth="1.5"/>
        <text x={145} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Ravigneaux Set</text>
        <text x={145} y={40} textAnchor="middle" fontSize="8.5" fill="#a04010">Two suns, shared ring, shared carrier</text>

        {(() => {
          const cx=145, cy=155, rS1=22, rS2=36, rR=98;
          const rPL=24, rPS=16;
          const pA=[90,210,330].map(d=>d*Math.PI/180);
          return (
            <g>
              <circle cx={cx} cy={cy} r={rR+12} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="2.5"/>
              <path d={ringPath(cx,cy,rR,36,rR*0.09)} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.3" opacity="0.65"/>
              {pA.map((a,i)=>{
                const px=cx+(rS2+rPL)*Math.cos(a), py=cy+(rS2+rPL)*Math.sin(a);
                return <g key={i}>
                  <path d={gearPath(px,py,rPL,10,rPL*0.19)} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
                  <circle cx={px} cy={py} r={rPL*0.25} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1"/>
                </g>;
              })}
              {pA.map((a,i)=>{
                const off=a+0.65;
                const px=cx+(rS1+rPS)*Math.cos(off), py=cy+(rS1+rPS)*Math.sin(off);
                return <g key={i}>
                  <path d={gearPath(px,py,rPS,7,rPS*0.19)} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.2"/>
                  <circle cx={px} cy={py} r={rPS*0.25} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1"/>
                </g>;
              })}
              <path d={gearPath(cx,cy,rS1,9)} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
              <circle cx={cx} cy={cy} r={rS1*0.4} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.7"/>
              <circle cx={cx} cy={cy} r={rS1*0.15} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
              <text x={cx} y={cy+4} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fdf4ec">S1</text>
              <circle cx={cx} cy={cy} r={rS2} fill="none" stroke="#c45a10" strokeWidth="2" strokeDasharray="3,2" opacity="0.5"/>
              <text x={cx+rS2+6} y={cy-6} fontSize="8" fill="#7a2e00">S2</text>
            </g>
          );
        })()}

        {/* Text below gear — starts at y=268 (155+98+12+3) */}
        <text x={145} y={278} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• Two sun gears (S1, S2)</text>
        <text x={145} y={291} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• Long &amp; short planets</text>
        <text x={145} y={304} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• Used in 4-speed auto gearboxes</text>
        <text x={145} y={317} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• 4 forward + 1 reverse in one set</text>

        {/* ── SIMPSON (centre) ── */}
        <rect x={305} y={10} width={270} height={330} rx={6} fill="none" stroke="#e8b07a" strokeDasharray="5,3" strokeWidth="1.5"/>
        <text x={440} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Simpson Set</text>
        <text x={440} y={40} textAnchor="middle" fontSize="8.5" fill="#a04010">Shared sun, two ring gears, two carriers</text>

        {(() => {
          const cx1=375, cx2=505, cy=155, rs=22, rp=18, rr=62;
          const pA=[90,210,330].map(d=>d*Math.PI/180);
          return (
            <g>
              <circle cx={cx1} cy={cy} r={rr+10} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="2"/>
              <path d={ringPath(cx1,cy,rr,24,rr*0.11)} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.3" opacity="0.7"/>
              {pA.map((a,i)=>{const px=cx1+(rs+rp)*Math.cos(a),py=cy+(rs+rp)*Math.sin(a);
                return <g key={i}><path d={gearPath(px,py,rp,8,rp*0.19)} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
                <circle cx={px} cy={py} r={rp*0.25} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1"/></g>;})}
              <circle cx={cx2} cy={cy} r={rr+10} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="2"/>
              <path d={ringPath(cx2,cy,rr,24,rr*0.11)} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.3" opacity="0.7"/>
              {pA.map((a,i)=>{const px=cx2+(rs+rp)*Math.cos(a),py=cy+(rs+rp)*Math.sin(a);
                return <g key={i}><path d={gearPath(px,py,rp,8,rp*0.19)} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
                <circle cx={px} cy={py} r={rp*0.25} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1"/></g>;})}
              <path d={gearPath(cx1,cy,rs,10)} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
              <path d={gearPath(cx2,cy,rs,10)} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
              <line x1={cx1} y1={cy} x2={cx2} y2={cy} stroke="#7a2e00" strokeWidth="5"/>
              <circle cx={cx1} cy={cy} r={rs*0.14} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
              <circle cx={cx2} cy={cy} r={rs*0.14} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
              {/* shared S label between the two gears, with bg rect to avoid overlap */}
              <rect x={(cx1+cx2)/2-24} y={cy-9} width={48} height={14} rx={3} fill="#fdf4ec" opacity="0.85"/>
              <text x={(cx1+cx2)/2} y={cy+1} textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#7a2e00">shared S</text>
            </g>
          );
        })()}

        <text x={440} y={278} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• One shared sun gear</text>
        <text x={440} y={291} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• Two separate ring + carrier sets</text>
        <text x={440} y={304} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• 3 forward speeds + reverse</text>
        <text x={440} y={317} textAnchor="middle" fontSize="8.5" fill="#7a4a28">• Classic 3-speed automatic gearbox</text>

        {/* ── COMPARISON TABLE (right) ── */}
        <rect x={600} y={10} width={290} height={330} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={745} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Quick Comparison</text>
        <line x1={608} y1={34} x2={882} y2={34} stroke="#e8b07a"/>
        {[
          ['', 'Ravigneaux', 'Simpson'],
          ['Suns', '2', '1 (shared)'],
          ['Rings', '1', '2'],
          ['Carriers', '1', '2'],
          ['Planet sets', '2 (long+short)', '2 (separate)'],
          ['Fwd ratios', '4 + R', '3 + R'],
          ['Axial length', 'Short', 'Longer'],
          ['Complexity', 'High', 'Medium'],
          ['Used in', 'ZF 4HP, GM THM', 'Ford C4, GM TH350'],
          ['', '', ''],
          ['Wilson set:', 'Ravigneaux variant with'],
          ['', 'additional clutch packs'],
          ['', 'for 5+ speed autos'],
          ['', ''],
          ['Lepelletier:', 'Ravigneaux + simple'],
          ['', 'planetary → 6 speeds'],
          ['', 'in compact package'],
        ].map(([lbl, v1, v2], i) => (
          <g key={i}>
            <text x={608} y={47+i*15} fontSize="8.5" fontWeight={i===0?'700':'600'} fill="#7a2e00">{lbl}</text>
            <text x={690} y={47+i*15} fontSize="8.5" fontFamily="'Courier New',monospace" fill={i===0?'#7a2e00':'#a04010'}>{v1}</text>
            {v2 && <text x={790} y={47+i*15} fontSize="8.5" fontFamily="'Courier New',monospace" fill={i===0?'#7a2e00':'#7a4a28'}>{v2}</text>}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Ravigneaux and Simpson sets achieve multiple ratios from a compact package by sharing members between two planetary stages</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Torque & Force Distribution
// ─────────────────────────────────────────────────────────────────────
function DiagramTorqueForce() {
  const W = 900, H = 310;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Torque Distribution, Planet Load Sharing &amp; Bearing Forces</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>
        <defs>
          <marker id="arTF" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
          <marker id="arTFb" markerWidth="7" markerHeight="7" refX="2" refY="3.5" orient="auto">
            <polygon points="6 0,0 3.5,6 7" fill="#7a2e00"/>
          </marker>
        </defs>

        {/* Force diagram: single planet with force vectors */}
        {(() => {
          const cx=200, cy=155, rs=38, rp=34, rr=110;
          const pA = 270 * Math.PI/180; // top planet
          const px = cx+(rs+rp)*Math.cos(pA), py=cy+(rs+rp)*Math.sin(pA);
          return (
            <g>
              {/* Ring outline only */}
              <circle cx={cx} cy={cy} r={rr+14} fill="none" stroke="#7a2e00" strokeWidth="2.5"/>
              <circle cx={cx} cy={cy} r={rr} fill="none" stroke="#c45a10" strokeWidth="1.5" strokeDasharray="4,3"/>
              {/* Two hidden planets (faded) */}
              {[30,150].map((deg,i)=>{
                const a=deg*Math.PI/180;
                const ppx=cx+(rs+rp)*Math.cos(a), ppy=cy+(rs+rp)*Math.sin(a);
                return <g key={i}>
                  <circle cx={ppx} cy={ppy} r={rp} fill="#e8b07a" opacity="0.3" stroke="#7a2e00" strokeWidth="1.5" strokeDasharray="3,3"/>
                </g>;
              })}
              {/* Carrier arm to top planet */}
              <line x1={cx} y1={cy} x2={px} y2={py} stroke="#a04010" strokeWidth="6" strokeLinecap="round" opacity="0.4"/>
              {/* Top planet */}
              <path d={gearPath(px,py,rp,12,rp*0.18)} fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
              <circle cx={px} cy={py} r={rp*0.28} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
              {/* Sun */}
              <path d={gearPath(cx,cy,rs,14)} fill="#c45a10" stroke="#7a2e00" strokeWidth="2.5"/>
              <circle cx={cx} cy={cy} r={rs*0.45} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.65"/>
              <circle cx={cx} cy={cy} r={rs*0.15} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>

              {/* Contact force at sun-planet mesh */}
              <line x1={px+2} y1={py+rp+4} x2={cx+rs+4} y2={cy-4}
                stroke="#c45a10" strokeWidth="2" markerEnd="url(#arTF)" strokeDasharray="4,2"/>
              <text x={(px+cx)/2+10} y={(py+cy)/2-10} fontSize="8.5" fontWeight="700" fill="#c45a10">W_SP</text>

              {/* Contact force at planet-ring mesh */}
              <line x1={px-2} y1={py-rp-4} x2={cx-rs/2} y2={cy-rr+10}
                stroke="#7a2e00" strokeWidth="2" markerEnd="url(#arTFb)" strokeDasharray="4,2"/>
              <text x={px-28} y={py-rp-10} fontSize="8.5" fontWeight="700" fill="#7a2e00">W_PR</text>

              {/* Carrier pin reaction force */}
              <line x1={px} y1={py} x2={px} y2={py+50}
                stroke="#a04010" strokeWidth="2" markerEnd="url(#arTF)"/>
              <text x={px+5} y={py+38} fontSize="8.5" fontWeight="700" fill="#a04010">F_pin</text>

              <text x={cx} y={cy + rr + 30} textAnchor="middle" fontSize="8.5" fill="#7a4a28">W_SP = W_PR (force equilibrium on planet)</text>
              <text x={cx} y={cy + rr + 42} textAnchor="middle" fontSize="8.5" fill="#7a4a28">F_pin = 2 × W_SP (resultant at carrier pin)</text>
            </g>
          );
        })()}

        {/* Formula panel */}
        <rect x={390} y={10} width={500} height={285} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={640} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Torque &amp; Force Equations</text>
        <line x1={398} y1={34} x2={882} y2={34} stroke="#e8b07a"/>

        {[
          ['Sun tangential force:', 'W_t_S = T_S / r_S'],
          ['Planet tangential force:', 'W_t_P = W_t_S  (same at mesh)'],
          ['Ring tangential force:', 'W_t_R = W_t_P = T_R / r_R'],
          ['', ''],
          ['Torque on sun:', 'T_S = W_t · r_S'],
          ['Torque on ring:', 'T_R = W_t · r_R = T_S · (N_R/N_S)'],
          ['Torque on carrier:', 'T_C = T_S + T_R = T_S·(1 + N_R/N_S)'],
          ['', '→ T_C = T_S · (N_S + N_R)/N_S'],
          ['', ''],
          ['Load per planet:', 'F_planet = W_t / n_p'],
          ['', 'n_p = number of planets (3 or 4)'],
          ['', '→ 3 planets = 1/3 load each'],
          ['', ''],
          ['Carrier pin force:', 'F_pin = 2 × W_t / n_p'],
          ['Pin bending moment:', 'M = F_pin × L/2  (L = pin length)'],
          ['', ''],
          ['Power balance:', 'P_S + P_R + P_C = 0  (ideal)'],
          ['', 'T_S·ω_S + T_R·ω_R + T_C·ω_C = 0'],
          ['', ''],
          ['Radial (separating):', 'W_r = W_t · tan(φ)  φ = pressure angle'],
          ['Resultant planet force:','F_res = W_t / cos(φ)'],
        ].map(([lbl, val], i) => (
          <g key={i}>
            <text x={398} y={46 + i*12} fontSize="8.5" fontWeight={lbl?'700':'400'} fill={lbl?'#7a2e00':'#7a4a28'}>{lbl}</text>
            <text x={lbl?545:398} y={46 + i*12} fontSize="8.5" fontFamily="'Courier New',monospace"
              fill={val.includes('→')||val.includes('1/3')?'#c45a10':'#a04010'}>{val}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Multiple planets share the load equally in an ideal gearbox — 3 planets give 1/3 the tooth load vs single planet pair, enabling the high torque density of planetary designs</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 7 — Efficiency vs Ratio & Loss Map
// ─────────────────────────────────────────────────────────────────────
function DiagramEfficiency() {
  const W = 900, H = 280;
  const stages = [
    { label:'1 stage', ratio:4,   eff:97.5, x:120 },
    { label:'1 stage', ratio:6,   eff:97.0, x:210 },
    { label:'2 stage', ratio:16,  eff:95.1, x:310 },
    { label:'2 stage', ratio:36,  eff:94.0, x:410 },
    { label:'3 stage', ratio:64,  eff:92.7, x:510 },
    { label:'3 stage', ratio:125, eff:91.3, x:610 },
    { label:'4 stage', ratio:256, eff:90.0, x:710 },
  ];
  const barBase=195, maxH=130, minEff=88, maxEff=100, barW=55;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Efficiency vs Overall Ratio — Planetary Gearbox Stages</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>

        {/* Axes */}
        <line x1={65} y1={58} x2={65} y2={barBase+2} stroke="#e8b07a" strokeWidth="1.5"/>
        <line x1={65} y1={barBase+2} x2={875} y2={barBase+2} stroke="#e8b07a" strokeWidth="1.5"/>
        {[88,90,92,94,96,98,100].map((v,i)=>{
          const y = barBase - ((v-minEff)/(maxEff-minEff))*maxH;
          return (
            <g key={i}>
              <line x1={60} y1={y} x2={875} y2={y} stroke="#e8c89a" strokeWidth="0.7" strokeDasharray="3,3"/>
              <text x={58} y={y+4} textAnchor="end" fontSize="8" fill="#7a4a28">{v}%</text>
            </g>
          );
        })}
        <text x={22} y={128} textAnchor="middle" fontSize="9" fill="#7a2e00" transform="rotate(-90,22,128)">Efficiency %</text>
        <text x={470} y={235} textAnchor="middle" fontSize="9" fill="#7a2e00">Overall Gear Ratio →</text>

        {/* Bars */}
        {stages.map((s,i)=>{
          const h = ((s.eff-minEff)/(maxEff-minEff))*maxH;
          return (
            <g key={i}>
              <rect x={s.x-barW/2} y={barBase-h} width={barW} height={h}
                fill="#c45a10" opacity={0.55+i*0.05} rx={3}/>
              <text x={s.x} y={barBase-h-6} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">{s.eff}%</text>
              <text x={s.x} y={barBase+13} textAnchor="middle" fontSize="8" fill="#a04010">i={s.ratio}</text>
              <text x={s.x} y={barBase+24} textAnchor="middle" fontSize="7.5" fill="#7a4a28">{s.label}</text>
            </g>
          );
        })}

        {/* Trend line */}
        <polyline
          points={stages.map(s=>`${s.x},${barBase-((s.eff-minEff)/(maxEff-minEff))*maxH}`).join(' ')}
          fill="none" stroke="#7a2e00" strokeWidth="2" strokeDasharray="5,3"/>

        {/* Loss breakdown panel */}
        <rect x={10} y={10} width={55} height={45} rx={3} fill="#fdf0e4" stroke="#e8b07a"/>
        <text x={37} y={22} textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#7a2e00">η per</text>
        <text x={37} y={32} textAnchor="middle" fontSize="7.5" fill="#7a2e00">stage:</text>
        <text x={37} y={44} textAnchor="middle" fontSize="8" fontWeight="700" fill="#c45a10">~97%</text>

        {/* Loss annotation panel */}
        <rect x={748} y={10} width={142} height={140} rx={5} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={819} y={26} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#7a2e00">Loss Sources</text>
        <line x1={755} y1={31} x2={882} y2={31} stroke="#e8b07a"/>
        {[
          ['Tooth mesh:', '~1.5–2%'],
          ['Bearing drag:', '~0.5–1%'],
          ['Churning:', '~0.3–0.8%'],
          ['Seal friction:', '~0.1–0.3%'],
          ['Total/stage:', '~2.5–4%'],
          ['', ''],
          ['η_total =', 'η_stage^n'],
          ['3 stages:', '0.97³=0.91'],
        ].map(([l,v],i)=>(
          <g key={i}>
            <text x={756} y={42+i*13} fontSize="8.5" fontWeight={l?'700':'400'} fill="#7a2e00">{l}</text>
            <text x={820} y={42+i*13} fontSize="8.5" fontFamily="'Courier New',monospace"
              fill={l==='η_total ='||l==='3 stages:'?'#c45a10':'#a04010'}>{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Efficiency decreases with each additional stage (~3% per stage) — for high-ratio applications, planetary stages offer better efficiency than equivalent worm-gear solutions</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 8 — Automatic Transmission Clutch Logic Table
// ─────────────────────────────────────────────────────────────────────
function DiagramTransmission() {
  const W = 900, H = 330;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Automatic Transmission — Gear Selection via Clutch &amp; Brake Engagement Logic</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>

        {/* Schematic of a 4-speed automatic using Ravigneaux set */}
        {/* Left schematic */}
        {(() => {
          const cx=160, cy=140, rs=30, rp=26, rr=88;
          const pA=[90,210,330].map(d=>d*Math.PI/180);
          const pC=pA.map(a=>({x:cx+(rs+rp)*Math.cos(a),y:cy+(rs+rp)*Math.sin(a)}));
          return (
            <g>
              {/* Ring */}
              <circle cx={cx} cy={cy} r={rr+14} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="2.5"/>
              <path d={ringPath(cx,cy,rr,34,rr*0.10)} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.7"/>
              {/* Carrier arms */}
              {pC.map((p,i)=><line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#a04010" strokeWidth="5" strokeLinecap="round" opacity="0.3"/>)}
              {/* Planets */}
              {pC.map((p,i)=>(
                <g key={i}>
                  <path d={gearPath(p.x,p.y,rp,11,rp*0.19,i*2.094)} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
                  <circle cx={p.x} cy={p.y} r={rp*0.28} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.2"/>
                </g>
              ))}
              {/* Sun */}
              <path d={gearPath(cx,cy,rs,12)} fill="#c45a10" stroke="#7a2e00" strokeWidth="2.5"/>
              <circle cx={cx} cy={cy} r={rs*0.44} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.8" opacity="0.65"/>
              <circle cx={cx} cy={cy} r={rs*0.15} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
              {/* Clutch/brake indicators around ring */}
              {[['C1','Clutch',0],['C2','Clutch',120],['B1','Brake',240]].map(([nm,tp,ang],i)=>{
                const rad=ang*Math.PI/180;
                const bx=cx+(rr+30)*Math.cos(rad), by=cy+(rr+30)*Math.sin(rad);
                return (
                  <g key={i}>
                    <rect x={bx-18} y={by-10} width={36} height={20} rx={4}
                      fill={tp==='Clutch'?'#c45a10':'#7a2e00'} opacity="0.85" stroke="#fdf4ec" strokeWidth="1"/>
                    <text x={bx} y={by+4} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fdf4ec">{nm}</text>
                  </g>
                );
              })}
              <text x={cx} y={cy+rr+36} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#7a2e00">Ravigneaux Planetary Set</text>
              <text x={cx} y={cy+rr+47} textAnchor="middle" fontSize="8" fill="#a04010">Orange = Clutch · Brown = Brake</text>
            </g>
          );
        })()}

        {/* Engagement table */}
        <rect x={320} y={10} width={565} height={305} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={602} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Typical 4-Speed Auto — Clutch/Brake Engagement Matrix</text>
        <line x1={328} y1={34} x2={877} y2={34} stroke="#e8b07a"/>

        {/* Table header */}
        {['Gear','Ratio','C1\n(Sun)','C2\n(Ring)', 'B1\n(Sun Brk)', 'B2\n(Ring Brk)', 'F1\n(OWC)'].map((h, i) => (
          <g key={i}>
            <rect x={328+i*80} y={36} width={80} height={30} fill="#a04010"/>
            {h.split('\n').map((l, li) => (
              <text key={li} x={368+i*80} y={48+li*11} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#fff">{l}</text>
            ))}
          </g>
        ))}

        {/* Table rows */}
        {[
          ['1st','3.00','—','—','●','—','●'],
          ['2nd','1.67','—','●','●','—','—'],
          ['3rd','1.00','●','●','—','—','—'],
          ['4th (OD)','0.75','●','—','—','●','—'],
          ['Rev','−2.50','—','—','—','●','—'],
          ['Neutral','—','—','—','—','—','—'],
        ].map((row, ri) => {
          const bg = ri%2===0?'#fdf4ec':'#fdf6ee';
          return (
            <g key={ri}>
              <rect x={328} y={66+ri*32} width={565} height={32} fill={bg}/>
              {row.map((cell, ci) => (
                <g key={ci}>
                  {cell === '●' ? (
                    <circle cx={368+ci*80} cy={82+ri*32} r={9}
                      fill={ci<=1?'#fdf4ec':ci===2||ci===3?'#c45a10':'#7a2e00'} stroke="#7a2e00" strokeWidth="1.5" opacity="0.85"/>
                  ) : (
                    <text x={368+ci*80} y={86+ri*32} textAnchor="middle" fontSize="9"
                      fontWeight={ci===0||ci===1?'700':'400'}
                      fill={cell===row[1]&&ci===1&&parseFloat(cell)<1?'#c45a10':ci===0?'#7a2e00':'#a04010'}>
                      {cell}
                    </text>
                  )}
                </g>
              ))}
            </g>
          );
        })}

        {/* Legend */}
        <circle cx={335} y={263} cy={270} r={7} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>
        <text x={348} y={274} fontSize="8.5" fill="#7a2e00">Clutch engaged</text>
        <circle cx={445} cy={270} r={7} fill="#7a2e00" stroke="#7a2e00" strokeWidth="1.5"/>
        <text x={458} y={274} fontSize="8.5" fill="#7a2e00">Brake applied</text>
        <text x={560} y={274} fontSize="8.5" fill="#7a4a28">OWC = One-Way Clutch (sprag)</text>

        <rect x={328} y={285} width={565} height={24} rx={3} fill="#fdf4ec" stroke="#e8b07a"/>
        <text x={610} y={297} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#7a2e00">
          Two clutches/brakes engaged simultaneously always defines the gear ratio uniquely
        </text>
        <text x={610} y={306} textAnchor="middle" fontSize="8" fill="#a04010">
          One-way clutch (OWC/sprag) allows automatic 1st→2nd under power without overlap
        </text>
      </svg>
      <div style={S.dCaption}>Automatic gearboxes select ratios by engaging different combinations of multi-plate clutches and band brakes — hydraulic or electro-hydraulic actuation controls timing</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 9 — Planet Count & Spacing Conditions
// ─────────────────────────────────────────────────────────────────────
function DiagramPlanetSpacing() {
  const W = 900, H = 270;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Planet Count, Assembly Condition &amp; Load-Sharing Comparison</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>

        {/* Draw 3-planet, 4-planet, 5-planet schematics */}
        {[3,4,5].map((np, ni) => {
          const cx = 100 + ni*185, cy=120, rs=28, rp=22, rr=74;
          const pA = Array.from({length:np},(_,i)=>(i*(2*Math.PI/np)-(Math.PI/2)));
          const pC = pA.map(a=>({x:cx+(rs+rp)*Math.cos(a),y:cy+(rs+rp)*Math.sin(a)}));
          return (
            <g key={ni}>
              <circle cx={cx} cy={cy} r={rr+12} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="2.5"/>
              <path d={ringPath(cx,cy,rr,Math.round(rr*0.56),rr*0.09)} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.3" opacity="0.65"/>
              {pC.map((p,i)=><line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#a04010" strokeWidth="4" strokeLinecap="round" opacity="0.3"/>)}
              {pC.map((p,i)=>(
                <g key={i}>
                  <path d={gearPath(p.x,p.y,rp,10,rp*0.19,i*2*Math.PI/np)} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
                  <circle cx={p.x} cy={p.y} r={rp*0.28} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.2"/>
                </g>
              ))}
              <path d={gearPath(cx,cy,rs,12)} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
              <circle cx={cx} cy={cy} r={rs*0.42} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.65"/>
              <circle cx={cx} cy={cy} r={rs*0.14} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>
              <text x={cx} y={cy+rr+22} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">{np} Planets</text>
              <text x={cx} y={cy+rr+35} textAnchor="middle" fontSize="8.5" fill="#a04010">Load/planet: 1/{np}</text>
              <text x={cx} y={cy+rr+47} textAnchor="middle" fontSize="8" fill="#7a4a28">
                {np===3?'Most common, simple balance':np===4?'Higher power density, harder to balance':'Highest density, complex assembly'}
              </text>
            </g>
          );
        })}

        {/* Assembly condition panel */}
        <rect x={655} y={10} width={235} height={245} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={772} y={28} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Assembly Conditions</text>
        <line x1={663} y1={34} x2={882} y2={34} stroke="#e8b07a"/>
        {[
          ['Equally-spaced', 'planets require:'],
          ['', '(N_S + N_R) / n_p'],
          ['', '= integer'],
          ['', ''],
          ['Example (3 planets):', ''],
          ['N_S=20, N_R=80:', '(20+80)/3 = 33.3 ✗'],
          ['N_S=20, N_R=40:', '(20+40)/3 = 20 ✓'],
          ['N_S=24, N_R=48:', '(24+48)/3 = 24 ✓'],
          ['', ''],
          ['For 4 planets:', '(N_S+N_R) ÷ 4 = int.'],
          ['For 5 planets:', '(N_S+N_R) ÷ 5 = int.'],
          ['', ''],
          ['Also need:', 'No tooth interference'],
          ['', 'between adjacent planets'],
          ['Min clearance:', 'r_p > (N_S+N_R)/n_p × sin(π/n_p)'],
          ['', ''],
          ['Phase condition:', 'For n_p planets of same'],
          ['', 'phase: N_S/n_p = integer'],
          ['', '(for simultaneous mesh)'],
        ].map(([lbl,val],i)=>(
          <g key={i}>
            <text x={663} y={46+i*11} fontSize="8" fontWeight={lbl?'700':'400'} fill={lbl?'#7a2e00':'#7a4a28'}>{lbl}</text>
            <text x={lbl?763:663} y={46+i*11} fontSize="8" fontFamily="'Courier New',monospace"
              fill={val.includes('✓')?'#a04010':val.includes('✗')?'#c45a10':'#a04010'}>{val}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>More planets = smaller tooth forces = higher torque density, but assembly conditions become more restrictive and manufacturing precision requirements increase</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 10 — Applications Overview
// ─────────────────────────────────────────────────────────────────────
function DiagramApplications() {
  const W = 900, H = 360;
  const apps = [
    { title:'Automatic\nGearbox', icon:'🚗', lines:['Ravigneaux/Simpson sets','4–10 forward ratios','Hydraulic clutch packs','ZF, Aisin, GM units'] },
    { title:'Wind Turbine\nNacelle', icon:'🌬️', lines:['3-stage planetary','100:1+ ratio up-set','Hub → generator','Multi-MW capacity'] },
    { title:'Robot Joint\nActuator', icon:'🦾', lines:['Cycloidal or planetary','High ratio compact','Backdriveable design','Precision positioning'] },
    { title:'Helicopter\nMain Rotor', icon:'🚁', lines:['Engine → rotor shaft','Large ratio reduction','Multi-stage planetary','Safety-critical design'] },
    { title:'Electric Vehicle\nDrive', icon:'⚡', lines:['Motor → wheel hub','Coaxial, compact','Fixed or 2-speed','Integrated in hub'] },
    { title:'Industrial\nPrecision Drive', icon:'⚙️', lines:['Servo motor output','Arcmin backlash','High torsional stiff.','Machine tool axes'] },
  ];
  // card height = 148, row gap = 20, 2 rows: total = 30 + 148 + 20 + 148 + 14 = 360
  const cardH = 148;
  const rowGap = 22;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Planetary Gearbox Applications Across Engineering Disciplines</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>
        {apps.map((a, i) => {
          const col = i % 3, row = Math.floor(i / 3);
          const x = 80 + col * 285, y = 18 + row * (cardH + rowGap);
          const lines = a.title.split('\n');
          return (
            <g key={i}>
              <rect x={x-65} y={y} width={146} height={cardH} rx={6}
                fill={i%2===0?'#fdf4ec':'#fdf0e4'} stroke="#e8b07a" strokeWidth="1.5"/>
              <circle cx={x} cy={y+32} r={26} fill="#c45a10" opacity="0.85" stroke="#7a2e00" strokeWidth="2"/>
              <text x={x} y={y+39} textAnchor="middle" fontSize="18">{a.icon}</text>
              {lines.map((l, li) => (
                <text key={li} x={x} y={y+68+li*14} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">{l}</text>
              ))}
              <line x1={x-55} y1={y+96} x2={x+55} y2={y+96} stroke="#e8c89a"/>
              {a.lines.map((l, li) => (
                <text key={li} x={x} y={y+108+li*12} textAnchor="middle" fontSize="8" fill="#a04010">{l}</text>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>Planetary gearboxes dominate applications where coaxial arrangement, high power density, and large ratios in a small envelope are simultaneously required</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────────────
export default function PlanetaryGearbox() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Planetary Gearbox Systems in Mechanical Engineering</h1>
      <p style={S.p}>
        A <strong>planetary (epicyclic) gearbox</strong> is a gear system consisting of one or more planet gears orbiting
        around a central sun gear, all enclosed within an outer ring gear (annulus), with planet positions maintained by
        a rotating carrier. Unlike parallel-shaft gear trains, all three members — sun, ring, and carrier — share a common
        rotational axis, making planetary systems the most power-dense and compact mechanical transmission arrangement
        available. Their unique topology allows multiple speed ratios from a single gear set by simply fixing, driving,
        or taking output from different members.
      </p>

      <InfoBox title="Quick Reference — Core Formulae" items={[
        "Tooth count rule:  N_R = N_S + 2·N_P  (fundamental geometry constraint — must hold exactly)",
        "Willis equation:  N_S·ω_S + N_R·ω_R = (N_S + N_R)·ω_C  (relates all three member speeds)",
        "Sun input, ring fixed:  i = 1 + N_R/N_S  (most common mode, reduction)",
        "Sun input, carrier fixed:  i = −N_R/N_S  (reverses direction)",
        "Ring input, sun fixed:  i = 1 + N_S/N_R  (smaller reduction)",
        "Multi-stage:  i_total = i₁ × i₂ × … × iₙ  ·  η_total = η₁ × η₂ × …",
        "Torque on carrier (sun input, ring fixed):  T_C = T_S × (N_S + N_R)/N_S = T_S × i",
        "Assembly condition for n_p equally-spaced planets:  (N_S + N_R) ÷ n_p = integer",
        "Power balance (ideal):  T_S·ω_S + T_R·ω_R + T_C·ω_C = 0  (all three members)",
        "Efficiency per stage ≈ 97% (spur/helical) · 3-stage: η ≈ 0.97³ ≈ 0.91",
      ]}/>

      <h2 style={S.h2}>Anatomy — Components &amp; Geometric Relationships</h2>
      <p style={S.p}>
        Every planetary set contains exactly three types of member: the <em>sun gear</em> at the centre, three or more
        <em> planet gears</em> meshing with both sun and ring, the <em>ring gear (annulus)</em> surrounding all planets,
        and the <em>planet carrier</em> which constrains planet centres at a fixed radius from the sun axis. All gears
        must share the same module for meshing to be geometrically possible.
      </p>
      <DiagramAnatomy />
      <Table2 rows={[
        ["Sun gear (S)","Central gear on the main axis · typically the high-speed input member\nSmallest gear in the set — highest tooth stress for a given torque\nN_S: typically 12–30 teeth for moderate ratios"],
        ["Planet gear (P)","Meshes with both sun and ring simultaneously · rotates on carrier pin\n3 planets standard · 4–6 for high-torque / high-precision applications\nN_P = (N_R − N_S) / 2  — determined by sun and ring choice"],
        ["Ring gear (Annulus, R)","Internal gear surrounding all planets · typically fixed to housing\nLargest gear — lowest tooth stress, highest torque capacity\nN_R = N_S + 2·N_P  — this identity is non-negotiable"],
        ["Planet carrier (C)","Rigid arm structure connecting all planet pins at equal angular spacing\nTypically the output member in reduction configurations\nMust withstand centrifugal load of orbiting planets at high speed"],
        ["Module requirement","All three gear types must have the same module m for correct mesh\nPitch diameters: d_S = m·N_S, d_P = m·N_P, d_R = m·N_R\nCentre distance check: r_S + 2·r_P = r_R (must be exact)"],
        ["Pressure angle","Standard 20° for most planetary gearboxes\nHigher pressure angle (25°) used in high-load gearboxes for stronger teeth\nAll gears in a stage must share the same pressure angle"],
      ]}/>

      <h2 style={S.h2}>Willis Equation &amp; Speed Ratio Derivation</h2>
      <p style={S.p}>
        The Willis equation is the fundamental relationship linking the angular velocities of all three members.
        It is derived by the superposition method: first imagining the carrier locked (standard gear ratio), then
        superposing a rigid-body rotation equal to the carrier speed. Any two member speeds and the tooth ratio
        completely determine the third member speed.
      </p>
      <DiagramWillis />
      <Table2 rows={[
        ["Willis equation","N_S·ω_S + N_R·ω_R = (N_S + N_R)·ω_C\nThree unknowns — fixing one member (setting its ω = 0) gives one equation, one unknown\nSolve for any member speed given the other and the gear ratio"],
        ["Superposition method","Step 1: Lock carrier (ω_C = 0) → standard gear ratio ω_R/ω_S = −N_S/N_R\nStep 2: Add ω_C to all members (rigid body rotation)\nStep 3: Combine — yields the Willis equation directly"],
        ["Positive & negative ratios","Positive ratio: output rotates same direction as input\nNegative ratio: output reverses direction (carrier fixed mode)\nSign convention: carrier fixed always gives reversal through planets"],
        ["Speed ratio (i)","i = ω_input / ω_output  (> 1 for reduction, < 1 for speed increase)\nAlternate notation: transmission ratio i vs gear ratio u — same quantity\nFor sun input, ring fixed: i = 1 + N_R/N_S  (always > 1, always positive — same direction)"],
        ["Differential mode","When no member is fixed, two inputs → one differential output\nω_C = (N_S·ω_S + N_R·ω_R) / (N_S + N_R)\nUsed in automotive differentials to split torque between wheels"],
        ["Direct drive (locked)","When any two members are locked together, all three rotate at the same speed\ni = 1 : 1, no torque multiplication, 100% mechanical efficiency\nUsed as top gear in some automatic transmissions"],
      ]}/>

      <h2 style={S.h2}>All Six Operating Modes</h2>
      <p style={S.p}>
        Three members each capable of being input, output, or fixed gives exactly six distinct operating modes
        (excluding the degenerate all-locked case). Each yields a different speed ratio and direction. Automatic
        transmissions exploit multiple modes by engaging clutches and brakes to fix or connect different members.
      </p>
      <DiagramModes />
      <Table2 rows={[
        ["Mode 1 — S in, R fixed, C out","Most common reduction mode · i = 1 + N_R/N_S\nTypical ratios: 3:1 to 6:1 per stage · output same direction as input\nUsed in: almost all industrial planetary reducers, automatic gearbox 1st gear"],
        ["Mode 2 — S in, C fixed, R out","Output reverses direction · i = −N_R/N_S  (magnitude > 1)\nHigh ratio but reversed — used for reverse gear in some automatic transmissions\nC fixed means carrier is grounded — planets spin but don't orbit"],
        ["Mode 3 — C in, R fixed, S out","Speed-increasing mode · i = (N_S+N_R)/N_S  (output faster than input)\nUsed in: wind turbines (slow hub → fast generator), overdrives\nSame ratio as mode 1 but inverted (reciprocal of same expression)"],
        ["Mode 4 — R in, S fixed, C out","Lower reduction ratio · i = 1 + N_S/N_R  (< mode 1)\nOutput same direction as input · gentler reduction\nUsed as a high gear when combined with other modes in multi-speed boxes"],
        ["Mode 5 — R in, C fixed, S out","Reversal and speed increase · i = −N_S/N_R  (< 1 in magnitude)\nPlanet carrier grounded — planets spin but don't orbit\nPractically useful for reverse/overdrive combinations"],
        ["Mode 6 — C in, S fixed, R out","Reduction with ring output · i = N_R/(N_S+N_R)  (< 1, speed increase)\nOutput same direction as input · sun acts as reaction member\nUsed in some hybrid drive units where carrier is driven by engine"],
      ]}/>

      <h2 style={S.h2}>Multi-Stage Planetary Gearboxes</h2>
      <p style={S.p}>
        For ratios beyond approximately 10:1 from a single stage, two or more stages are connected in series.
        The carrier output of each stage drives the sun gear of the next. All ring gears are typically fixed to a
        common housing. Ratios multiply, giving very high reduction (50:1–300:1) in a surprisingly compact envelope.
      </p>
      <DiagramMultiStage />
      <Table2 rows={[
        ["Series connection","Output carrier of stage N → input sun of stage N+1 via rigid shaft\nOverall ratio: i_total = i₁ × i₂ × …  (product of all stage ratios)\nAll stages coaxial — output shaft coincides with input shaft axis"],
        ["Stage ratio selection","Practical per-stage ratio: 3:1 to 10:1 for spur/helical planets\nEqual stages minimise overall size: i_stage = ∜(i_total) for 4 stages\nUnequal stages can optimise efficiency or minimise first-stage size (lightest)"],
        ["Shared ring gear","Some designs use a single long ring gear spanning multiple stages\nReduces housing complexity but constrains ratio equality between stages\nCommon in compact servo planetary reducers (Neugart, Apex, Heason)"],
        ["Backlash in series","Backlash multiplies additively across stages, not multiplicatively\nTwo-stage with 3 arcmin each: total backlash ≈ 3+3 = ~6 arcmin\nPrecision reducers: < 3 arcmin total (zero-backlash designs: < 1 arcmin)"],
        ["Torque flow","First stage carries lowest torque (highest speed)\nLast stage carries highest torque · largest gears, widest face width\nPower flow analysis critical — first stage often determines efficiency"],
        ["Inline vs right-angle","Multi-stage planetary remains inline (coaxial) regardless of stage count\nRight-angle output requires a bevel or hypoid stage at input or output\nL-shaped gearboxes: planetary stages + orthogonal bevel stage common in robotics"],
      ]}/>

      <h2 style={S.h2}>Compound Planetary Sets — Ravigneaux &amp; Simpson</h2>
      <p style={S.p}>
        Rather than simply stacking independent planetary stages, automatic transmission designers developed compact
        compound sets that share members between stages, enabling multiple gear ratios from a single axial space. The
        Ravigneaux and Simpson sets are the canonical examples, forming the core of most 20th-century automatic gearboxes.
      </p>
      <DiagramCompoundSets />
      <Table2 rows={[
        ["Ravigneaux set","Two sun gears (different sizes) + one shared ring gear + one shared carrier\nTwo sets of planets: long planets mesh with large sun and ring; short planets mesh with small sun and long planets\nUp to 4 forward ratios + reverse from a single compact set · used in ZF 4HP, Ford AOD, GM THM400"],
        ["Simpson set","One shared sun gear + two ring gears + two carriers (connected)\nCarrier of stage 1 connected to ring of stage 2 and vice versa\nProvides 3 forward + 1 reverse · Ford C4/C6, GM TH350/TH400 — millions produced"],
        ["Wilson gearset","Ravigneaux-based with additional clutch elements for 5 speeds\nUsed in some Rolls-Royce and military vehicle transmissions\nMore complex but very smooth ratio steps in a small package"],
        ["Lepelletier gearset","Single simple planetary + Ravigneaux set → 6 forward speeds + 1 reverse\nUsed in ZF 6HP series (widely used in BMW, Audi, Jaguar, etc.)\nOnly 5 shift elements needed — one always open → improved efficiency"],
        ["Modern 8–10 speed","Multiple compound planetary sets with 5–7 clutch/brake elements\nZF 8HP: 4 planetary sets, 5 clutches/brakes → 8 speeds + reverse\nAisin 10-speed: 4 planetary rows → 10 forward ratios in same envelope as old 6-speed"],
      ]}/>

      <h2 style={S.h2}>Torque &amp; Force Distribution</h2>
      <DiagramTorqueForce />
      <Table2 rows={[
        ["Torque split","Input torque split equally among n_p planets (ideal)\nEach planet pin carries 1/n_p of total tangential force\nThis load-sharing is why planetary gearboxes are so compact for their torque rating"],
        ["Force on planet pin","F_pin = 2 × W_t / n_p  (factor 2: planet is in mesh at two points simultaneously)\nPin must resist bending: M = F_pin × L_pin/2\nPin fatigue life governs gearbox overhaul interval in heavy-duty applications"],
        ["Radial separating force","W_r = W_t × tan(φ)  — acts radially, loads carrier and planet bearings\nCancels around ring gear (symmetric planets) → no net radial force on ring bearing\nNet carrier bearing load = sum of all radial forces (cancels for equally-spaced planets)"],
        ["Torque triangle","T_S : T_R : T_C = N_S : N_R : (N_S+N_R)  (for all cases where power flows through all members)\nT_C = T_S + T_R  — carrier receives sum of sun and ring torques\nThis triangle is constant regardless of which member is input/output/fixed"],
        ["Planet bearing load","Each planet pin bearing carries the resultant of sun-mesh and ring-mesh forces\nFor 20° pressure angle: F_resultant = F_tangential / cos(20°) ≈ 1.064 × F_tangential\nBearing selection: L₁₀ life calculation at operating speed and force"],
        ["Power recirculation","In some configurations, power recirculates internally (negative power flows)\nOccurs when one mesh absorbs power from another — reduces efficiency significantly\nDesign check: verify no negative power paths in intended operating mode"],
      ]}/>

      <h2 style={S.h2}>Efficiency &amp; Loss Analysis</h2>
      <DiagramEfficiency />
      <Table2 rows={[
        ["Per-stage efficiency","Spur/helical: η ≈ 0.97–0.985 per stage\nHelical slightly lower than spur (axial thrust losses in thrust bearings)\nWorm-equivalent: a 3-stage planetary at 0.91 beats a worm gear at 50–90% for same ratio"],
        ["Tooth mesh loss","Dominant loss: ~1.5–2% per mesh\nSliding friction during approach/recess phases · rolling at pitch point only\nReduced by: ground teeth, low-viscosity oil, correct tooth profile"],
        ["Churning loss","Planets orbiting at high speed churn gearbox oil\nSignificant at >2000 rpm carrier speed · oil viscosity and level critical\nJet lubrication preferred over splash for high-speed stages"],
        ["Efficiency vs ratio","Higher ratio (more stages) → lower overall efficiency\nParabolic degradation: 0.97^n per stage rule\nFor i=100:1 single-stage worm: η≈50% vs 3-stage planetary: η≈91% → planetary wins"],
        ["Power recirculation loss","Some compound set configurations have internal power recirculation\nCirculating power can exceed input power → efficiency collapses\nAvoid by checking power flow direction at design stage using Willis equation"],
        ["Lubrication regime","Below 1000 rpm: oil mist or grease · 1000–5000 rpm: splash\nAbove 5000 rpm: pressure-fed jet lubrication essential\nSAE 80W-90 (industrial) or ATF (automotive) · synthetic oils reduce churning loss 20–30%"],
      ]}/>

      <h2 style={S.h2}>Automatic Transmission — Clutch Logic &amp; Gear Selection</h2>
      <DiagramTransmission />
      <Table2 rows={[
        ["Multi-plate clutch pack","Stack of alternating steel and friction-lined discs under hydraulic pressure\nEngaging clamps pack → rotational lock between two members\nDisengaging releases pressure → spring returns → free rotation"],
        ["Band brake","Steel band wraps around drum (connected to one gearset member)\nHydraulic servo tightens band → grounds (fixes) that member\nFaster engagement than clutch packs — used for 1st gear hold and reverse"],
        ["One-way clutch (OWC)","Sprag or roller ramp mechanism — transmits torque one direction only\nAllows seamless 1→2 upshift under power without hydraulic overlap\nFree-wheels on overrun → no engine braking in that gear (unless additional lock-up provided)"],
        ["Hydraulic control","Traditional: valve body with pressure-regulating spool valves and shift valves\nModern: electro-hydraulic solenoid valves controlled by TCU (transmission control unit)\nPressure management critical: too low → clutch slip; too high → harsh shift"],
        ["Torque converter","Fluid coupling between engine and gearbox — allows slip for smooth starting\nLock-up clutch engages at cruise speed → eliminates slip → improves fuel economy\nStall torque multiplication: up to 2.5:1 on initial take-off (augments first gear ratio)"],
        ["Dual-clutch transmission","Two separate input shafts each with its own friction clutch (wet or dry)\nOdd gears on shaft 1, even gears on shaft 2 · next gear pre-selected\nDog clutches (synchromesh) select gears · DCT = automated manual with dog clutch selection"],
      ]}/>

      <h2 style={S.h2}>Planet Count, Spacing &amp; Assembly Conditions</h2>
      <DiagramPlanetSpacing />
      <Table2 rows={[
        ["3-planet standard","Most common: 120° spacing, balanced centrifugally, simple carrier design\nAssembly condition: (N_S + N_R) divisible by 3\nUsed in: virtually all industrial and automotive planetary gearboxes"],
        ["4-planet arrangement","15% more torque capacity than 3-planet for same gear size\nAssembly condition: (N_S + N_R) divisible by 4\nManufacturing challenge: must maintain equal load sharing — requires tight tolerances"],
        ["5+ planet arrangement","Used in very high-torque applications: wind turbines, large industrial drives\nAssembly condition: (N_S + N_R) divisible by 5\nRequires floating sun gear (self-aligning) to achieve load sharing despite manufacturing errors"],
        ["Floating members","For > 3 planets, tolerances prevent perfect equal load sharing\nFloating sun: sun gear has no fixed radial bearing — self-centres among planets\nFloating carrier: similar; allows self-equalisation of planet loads"],
        ["Inter-planet clearance","Adjacent planets must not interfere: d_p < 2 × (r_s + r_p) × sin(π/n_p)\nMinimum gap typically 0.5–1 mm between planet tip circles\nLimits maximum planet size for a given planet count and sun/ring geometry"],
        ["Phase (timing) condition","For meshing all planets simultaneously: N_S / n_p = integer (for in-phase design)\nOut-of-phase assembly: planets mesh at different sun tooth positions → acceptable\nOut-of-phase can reduce vibration by distributing mesh excitation events"],
      ]}/>

      <h2 style={S.h2}>Design Procedure</h2>
      <Table2 rows={[
        ["Step 1 — Define requirements","Input speed, output speed (→ ratio i), peak & continuous torque, service factor\nSpace envelope: max OD, max length · bearing arrangement · environment"],
        ["Step 2 — Select operating mode","Most applications: sun input, ring fixed, carrier output (mode 1)\nCalculate required N_R/N_S = i − 1\nFor multi-stage: distribute ratio equally: i_stage = i_total^(1/n)"],
        ["Step 3 — Choose module & planet count","Select module m from standard series (1, 1.25, 1.5, 2, 2.5, 3…) based on torque\nChoose n_p (usually 3 for simplicity; 4–6 for high torque)\nMinimum sun teeth: N_S_min ≈ 12–16 to avoid undercutting"],
        ["Step 4 — Calculate tooth counts","From i = 1 + N_R/N_S: N_R = (i−1)·N_S\nN_P = (N_R − N_S)/2 (must be integer)\nCheck assembly condition: (N_S + N_R) ÷ n_p = integer"],
        ["Step 5 — Verify geometry","Pitch diameters: d_S=mN_S, d_R=mN_R, d_P=mN_P\nCheck: r_S + 2r_P = r_R ✓\nCheck inter-planet clearance: no adjacent planet interference"],
        ["Step 6 — Stress analysis","Sun tooth bending: σ_S = W_t/(m·b·Y_S) · K_A · K_v · K_H\nRing tooth contact stress (Hertz): σ_H = Z_E · √(F_t·K_A/(b·d_P) · (1/ρ₁ ± 1/ρ₂))\nCarrier pin: bending + shear at planet pin bore · fatigue life calculation"],
        ["Step 7 — Bearing selection","Sun shaft: deep-groove ball bearing (carries torque, low radial load)\nPlanet pin: needle roller bearings (high radial load, limited axial space)\nCarrier output: tapered roller or angular contact for combined loads"],
        ["Step 8 — Lubrication & seals","Splash (< 2000 rpm) or pressure-jet (> 2000 rpm carrier speed)\nOil grade: ISO VG 220–460 (industrial); ATF Dexron/Mercon (automotive)\nSeal selection: lip seals for low speed; labyrinth + exclusion seal for high speed"],
      ]}/>

      <h2 style={S.h2}>Applications</h2>
      <DiagramApplications />
      <Table2 rows={[
        ["Automotive transmission","All automatic gearboxes since 1940s use planetary sets\nModern 8–10 speed units: 4 planetary rows + 5 shift elements\nDual-clutch: planetary carrier connected to differential for final drive"],
        ["Wind turbine drivetrain","Hub (10–20 rpm) → 3-stage planetary → generator (1000–1800 rpm)\nGearbox ratio 50:1–100:1 · 3–5 MW transmissible · extreme fatigue loading\nDirect-drive alternative (permanent magnet generator) avoids gearbox but needs larger generator"],
        ["Robotics & servo drives","Planetary reducer on each joint axis: 50:1–160:1 per stage typical\nHarmonic drive (wave generator) and cycloidal drive compete for highest precision\nBacklash < 1 arcmin for precision robots; torsional stiffness critical"],
        ["Helicopter main gearbox","Engine (~20,000–30,000 rpm gas turbine) → 100:1+ reduction → main rotor (200–400 rpm)\nMulti-stage planetary + bevel stage for tail rotor\nMission-critical: dual oil systems, magnetic chip detectors, 30-min run-dry capability"],
        ["Electric vehicle hub motor","Planetary reducer integrated in wheel hub: motor at high speed → wheel at low\nRatio 8:1–15:1 typical · coaxial, no external shafting\nCompact: entire drive-unit inside wheel rim; regenerative braking through same path"],
        ["Industrial servo gearbox","Precision planetary reducers (Neugart, Apex, Heason, Stöber) for CNC machines\nArcmin-class backlash: < 3 arcmin standard, < 1 arcmin precision grade\nFlange-mount output; IEC/NEMA motor input; sizes 40–500 Nm rated output torque"],
      ]}/>

      <h2 style={S.h2}>Failure Modes &amp; Maintenance</h2>
      <Table2 rows={[
        ["Planet bearing failure","Most common failure mode in industrial planetary gearboxes\nNeedle roller bearings fatigue under high cyclical loading at each planet pin\nDetection: vibration signature at planet mesh frequency = N_P × n_carrier × rpm"],
        ["Tooth pitting (surface fatigue)","Contact stress cycles on planet flanks from both sun and ring mesh\nPlanets have twice the load cycles per revolution as sun or ring teeth\nFix: shot-peened and case-hardened planets to 58–62 HRC surface"],
        ["Ring gear fretting","Ring fixed to housing — fretting corrosion at housing bore interface\nResults in ring loosening, rotation, catastrophic loss of ratio\nPrevention: interference fit + locking pins; inspect at major overhaul"],
        ["Carrier pin bending","High bending moment at planet pin under heavy or shock load\nFatigue crack initiates at pin/carrier junction stress concentration\nInspection: magnetic particle test at overhaul; replace pins at service interval"],
        ["Oil contamination","Metal particles from wear accelerate abrasive damage in tight clearances\nMagnetic sump plugs capture ferrous debris — inspect at each oil change\nFilter bypassing: change filter at specified interval regardless of appearance"],
        ["Inspection schedule","Industrial: oil analysis every 2000 h; bearing replacement at 20,000 h\nAutomotive: ATF change every 60,000 km (conventional) / 100,000 km (synthetic)\nVibration monitoring: track mesh frequencies and bearing defect frequencies via FFT"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Geometry:  N_R = N_S + 2·N_P  ·  r_R = r_S + 2·r_P  ·  m must be equal for all gears",
        "Willis:  N_S·ω_S + N_R·ω_R = (N_S + N_R)·ω_C",
        "Mode 1 (S in, R fixed, C out):  i = 1 + N_R/N_S",
        "Mode 2 (S in, C fixed, R out):  i = −N_R/N_S  (reversal)",
        "Torque ratio:  T_C = T_S·(N_S+N_R)/N_S = T_S·i  (mode 1)",
        "Torque triangle:  T_S + T_R = T_C  (signs as per Willis convention)",
        "Planet load share:  F_per_planet = F_total / n_p  (ideal, equal sharing)",
        "Assembly condition:  (N_S + N_R) / n_p = integer  (for equally-spaced planets)",
        "Multi-stage ratio:  i_total = i₁ × i₂ × … × iₙ",
        "Efficiency:  η_total = η_stage^n  ·  η_stage ≈ 0.97–0.985 for spur/helical",
        "Power balance:  T_S·ω_S + T_R·ω_R + T_C·ω_C = 0  (ideal, no losses)",
        "Planet phase:  N_S / n_p = integer  (for in-phase simultaneous assembly)",
      ]}/>
    </div>
  );
}